import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { PopupContainerProvider } from '@/components/ReportForm/Container/PopupContainer';
import { PictureInPictureModeProvider } from '@/context/InvoiceReports';
import { useGetOfficeInvoiceReportSettings } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { useBulkDeleteModal } from '@/features/invoice_reports/components/ConfirmDeleteModal/hooks/useBulkDeleteModal';
import type { InvoiceReportsSearchQuery } from '@/features/invoice_reports/types';
import { buildOrderByArray } from '@/features/invoice_reports/utils/buildOrderByArray';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { Search } from '../Search';
import { InvoiceReportsButtonGroup } from './ButtonGroups';
import {
  useImportDrawer,
  useInvoiceReportsFormInitial,
  useInvoiceReportsHandleSearchQuery,
} from './hooks';
import { useContainerHeight } from './hooks/useContainerHeight';
import { useExportInvoiceReportsQuery } from './hooks/useExportInvoiceReportsQuery';
import { PaymentRequestImportDrawer } from './ImportDrawer';
import styles from './List.module.scss';
import { useInvoiceReportsSearchData } from './Table/hook';
import { InvoiceReportsTableView } from './Table/Table';

const cx = classnames.bind(styles);

export const InvoiceReports: FC = memo(() => {
  const { data: officeInvoiceReportSettings } =
    useGetOfficeInvoiceReportSettings();

  // When API errors, default to false (proxy OFF) so ProxyOffButtonGroup is shown
  const canAgentEntry =
    officeInvoiceReportSettings?.data?.office_member_setting?.can_agent_entry ??
    false;
  const reportTypeId =
    officeInvoiceReportSettings?.data?.invoice_report_type?.id;
  const importInvoiceReportSampleCsvPaths =
    officeInvoiceReportSettings?.data?.import_invoice_report_sample_csv_paths;
  const noPayeeTooltip =
    officeInvoiceReportSettings?.data?.new_invoice_report_error_messages?.[0];
  const isDutyFree = officeInvoiceReportSettings?.data?.duty_free ?? false;

  const defaultValues = useInvoiceReportsFormInitial(canAgentEntry);
  const { searchQuery, onSearch, handleMultiColumnSort, sortOrders } =
    useInvoiceReportsHandleSearchQuery(defaultValues);

  // Use custom hook for container height
  const { containerRef, containerStyle } = useContainerHeight();

  // Memoize table query with extracted helper function
  const tableQuery = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, sort_fields, sort_orders, ...searchParams } = searchQuery;
    const order_by = buildOrderByArray(sortOrders);

    return {
      query: searchParams,
      page,
      order_by,
    };
  }, [searchQuery, sortOrders]);

  const [
    { setError },
    ErrorContextHolder,
    ErrorInlineNotificationProvider,
    onClearMessage,
  ] = useErrorInlineNotification();

  const { data, pagination, refetch, isFetching } = useInvoiceReportsSearchData(
    tableQuery,
    setError
  );

  const { handleExport, isLoading: isExportLoading } =
    useExportInvoiceReportsQuery(tableQuery);
  const isExportDisabled = data.length === 0 && !isFetching;

  // Stable search handler
  const onHandleSearch = useCallback(
    (searchData: InvoiceReportsSearchQuery) => {
      onClearMessage();
      onSearch(searchData, refetch);
    },
    [onSearch, onClearMessage, refetch]
  );

  // Memoize container class name
  const containerClassName = cx(styles['container']);
  const notificationClassName = cx(styles['inline-notification-wrap']);
  const isApplyMbToClassName = useMemo(() => {
    return (pagination.maxPage === 1 || data.length === 0) && !isFetching;
  }, [pagination.maxPage, data.length, isFetching]);

  const {
    open: importDrawerOpen,
    onClose: closeImportDrawer,
    onOpenImport: openImportDrawer,
    onOpenImportProxy: openImportProxyDrawer,
    isProxyImport,
  } = useImportDrawer();

  // close drawer, refetch, go to page 1
  const onImportSuccess = useCallback(() => {
    closeImportDrawer();
    refetch();
    onHandleSearch({ page: 1 });
  }, [closeImportDrawer, refetch, onHandleSearch]);

  // Selection for bulk delete (reported by table; UI lives in header row with Import/Export)
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectedCount = selectedIds.length;

  const handleSelectionChange = useCallback((_count: number, ids: string[]) => {
    setSelectedIds(ids);
  }, []);

  const {
    bulkDeleteModalOpen,
    bulkDeleteCount,
    bulkDeleteIds,
    onOpenBulkDeleteModal,
    onCloseBulkDeleteModal,
  } = useBulkDeleteModal();

  const onBulkDelete = useCallback(() => {
    onOpenBulkDeleteModal(selectedCount, selectedIds);
  }, [onOpenBulkDeleteModal, selectedCount, selectedIds]);

  return (
    <PictureInPictureModeProvider>
      <PopupContainerProvider>
        <div
          className={containerClassName}
          ref={containerRef}
          style={containerStyle}
        >
          <ErrorInlineNotificationProvider value={setError}>
            <div className={notificationClassName}>{ErrorContextHolder}</div>
            <Search
              defaultValues={defaultValues}
              onSearch={onHandleSearch}
              searchQuery={searchQuery}
              isLoading={isFetching}
              officeInvoiceReportSettings={officeInvoiceReportSettings?.data}
            />
            <InvoiceReportsButtonGroup
              canAgentEntry={canAgentEntry}
              reportTypeId={reportTypeId}
              containerClassName={isApplyMbToClassName ? cx(['header_mb']) : ''}
              onExport={handleExport}
              isExportDisabled={isExportDisabled}
              isExportLoading={isExportLoading}
              onImport={openImportDrawer}
              onImportProxyApplication={openImportProxyDrawer}
              selectedCount={selectedCount}
              onBulkDelete={onBulkDelete}
              isFetching={isFetching}
              noPayeeTooltip={noPayeeTooltip}
            />
            <PaymentRequestImportDrawer
              open={importDrawerOpen}
              onClose={closeImportDrawer}
              onImportSuccess={onImportSuccess}
              canAgentEntry={isProxyImport}
              importInvoiceReportSampleCsvPaths={
                importInvoiceReportSampleCsvPaths
              }
              refetch={refetch}
            />
            <InvoiceReportsTableView
              searchQuery={searchQuery}
              onSearch={onHandleSearch}
              onMultiColumnSort={handleMultiColumnSort}
              sortOrders={sortOrders}
              query={tableQuery}
              data={data}
              pagination={pagination}
              isFetching={isFetching}
              canAgentEntry={canAgentEntry}
              refetch={refetch}
              onSelectionChange={handleSelectionChange}
              bulkDeleteModalOpen={bulkDeleteModalOpen}
              bulkDeleteCount={bulkDeleteCount}
              bulkDeleteIds={bulkDeleteIds}
              onCloseBulkDeleteModal={onCloseBulkDeleteModal}
              isDutyFree={isDutyFree}
            />
          </ErrorInlineNotificationProvider>
        </div>
      </PopupContainerProvider>
    </PictureInPictureModeProvider>
  );
});

InvoiceReports.displayName = 'InvoiceReports';
